import { useState, useEffect } from "react";
import { Plus, ChevronLeft, Users, Wifi } from "lucide-react";
import { adminApi } from "../../api";
import { Button, LoadingText } from "../ui";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  created_at: string;
}

interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  role?: { name: string } | null;
}

interface SlackConfig {
  tenant_id: string;
  channel_ids: string | null;
  has_bot_token: boolean;
  has_signing_secret: boolean;
}

type View = "list" | "detail" | "create";

export default function TenantsSettings() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>("list");
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [slackConfig, setSlackConfig] = useState<SlackConfig | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create form state
  const [createName, setCreateName] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createSlugTouched, setCreateSlugTouched] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Slack edit state
  const [editingSlack, setEditingSlack] = useState(false);
  const [slackBotToken, setSlackBotToken] = useState("");
  const [slackSigningSecret, setSlackSigningSecret] = useState("");
  const [slackChannelIds, setSlackChannelIds] = useState("");
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackError, setSlackError] = useState<string | null>(null);

  useEffect(() => {
    loadTenants();
  }, []);

  async function loadTenants() {
    setLoading(true);
    try {
      const res = await adminApi.listTenants();
      setTenants(res.data);
    } finally {
      setLoading(false);
    }
  }

  async function openTenant(tenant: Tenant) {
    setSelectedTenant(tenant);
    setView("detail");
    setDetailLoading(true);
    try {
      const [usersRes, slackRes] = await Promise.all([
        adminApi.listTenantUsers(tenant.id),
        adminApi.getTenantSlack(tenant.id),
      ]);
      setTenantUsers(usersRes.data);
      setSlackConfig(slackRes.data);
      setSlackChannelIds(slackRes.data.channel_ids ?? "");
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleToggleActive(tenant: Tenant) {
    await adminApi.updateTenant(tenant.id, { is_active: !tenant.is_active });
    loadTenants();
    if (selectedTenant?.id === tenant.id) {
      setSelectedTenant({ ...tenant, is_active: !tenant.is_active });
    }
  }

  async function handleCreate() {
    setCreateError(null);
    if (!createName.trim() || !createSlug.trim()) {
      setCreateError("Name and slug are required");
      return;
    }
    setCreating(true);
    try {
      await adminApi.createTenant({ name: createName.trim(), slug: createSlug.trim() });
      setCreateName("");
      setCreateSlug("");
      setCreateSlugTouched(false);
      setView("list");
      loadTenants();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setCreateError(msg ?? "Failed to create tenant");
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveSlack() {
    if (!selectedTenant) return;
    setSlackSaving(true);
    setSlackError(null);
    try {
      const payload: Record<string, string | undefined> = {
        channel_ids: slackChannelIds || undefined,
      };
      if (slackBotToken) payload.bot_token = slackBotToken;
      if (slackSigningSecret) payload.signing_secret = slackSigningSecret;
      const res = await adminApi.updateTenantSlack(selectedTenant.id, payload);
      setSlackConfig(res.data);
      setSlackBotToken("");
      setSlackSigningSecret("");
      setEditingSlack(false);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSlackError(msg ?? "Failed to save Slack config");
    } finally {
      setSlackSaving(false);
    }
  }

  function autoSlug(name: string) {
    return name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  }

  if (loading) return <LoadingText />;

  if (view === "create") {
    return (
      <div className="space-y-6 max-w-lg">
        <button
          type="button"
          onClick={() => { setView("list"); setCreateError(null); setCreateSlugTouched(false); }}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ChevronLeft className="w-4 h-4" /> Back to tenants
        </button>
        <h2 className="text-lg font-semibold">Create new tenant</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Acme Corp"
              value={createName}
              onChange={(e) => {
                setCreateName(e.target.value);
                if (!createSlugTouched) setCreateSlug(autoSlug(e.target.value));
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input
              className="w-full border rounded px-3 py-2 text-sm font-mono"
              placeholder="acme"
              value={createSlug}
              onChange={(e) => { setCreateSlugTouched(true); setCreateSlug(e.target.value); }}
            />
            <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers and hyphens only.</p>
          </div>
          {createError && <p className="text-sm text-red-600">{createError}</p>}
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? "Creating…" : "Create tenant"}
          </Button>
        </div>
      </div>
    );
  }

  if (view === "detail" && selectedTenant) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setView("list")}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ChevronLeft className="w-4 h-4" /> Back to tenants
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{selectedTenant.name}</h2>
            <p className="text-sm text-gray-500 font-mono">{selectedTenant.slug}</p>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium ${
              selectedTenant.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
            }`}
          >
            {selectedTenant.is_active ? "Active" : "Inactive"}
          </span>
        </div>

        {detailLoading ? (
          <LoadingText />
        ) : (
          <>
            {/* Users */}
            <section>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                <Users className="w-4 h-4" /> Users ({tenantUsers.length})
              </h3>
              <div className="border rounded-lg divide-y overflow-hidden">
                {tenantUsers.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-gray-400">No users in this tenant.</p>
                ) : (
                  tenantUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between px-4 py-2 text-sm">
                      <div>
                        <p className="font-medium">{u.full_name}</p>
                        <p className="text-gray-500 text-xs">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {u.role && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {u.role.name}
                          </span>
                        )}
                        {!u.is_active && (
                          <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded">inactive</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* Slack config */}
            <section>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                <Wifi className="w-4 h-4" /> Slack integration
              </h3>
              {!editingSlack ? (
                <div className="border rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bot token</span>
                    <span className={slackConfig?.has_bot_token ? "text-green-600" : "text-gray-400"}>
                      {slackConfig?.has_bot_token ? "Configured" : "Not set"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Signing secret</span>
                    <span className={slackConfig?.has_signing_secret ? "text-green-600" : "text-gray-400"}>
                      {slackConfig?.has_signing_secret ? "Configured" : "Not set"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Channel IDs</span>
                    <span className="text-gray-700 font-mono text-xs">{slackConfig?.channel_ids ?? "—"}</span>
                  </div>
                  <Button variant="secondary" onClick={() => setEditingSlack(true)} className="mt-2">
                    Edit Slack config
                  </Button>
                </div>
              ) : (
                <div className="border rounded-lg p-4 space-y-3 text-sm">
                  <div>
                    <label className="block text-gray-600 mb-1">Bot token (leave blank to keep existing)</label>
                    <input
                      type="password"
                      className="w-full border rounded px-3 py-2 text-sm font-mono"
                      placeholder="xoxb-…"
                      value={slackBotToken}
                      onChange={(e) => setSlackBotToken(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 mb-1">Signing secret (leave blank to keep existing)</label>
                    <input
                      type="password"
                      className="w-full border rounded px-3 py-2 text-sm font-mono"
                      value={slackSigningSecret}
                      onChange={(e) => setSlackSigningSecret(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 mb-1">Channel IDs (comma-separated)</label>
                    <input
                      className="w-full border rounded px-3 py-2 text-sm font-mono"
                      placeholder="C1234567,C8901234"
                      value={slackChannelIds}
                      onChange={(e) => setSlackChannelIds(e.target.value)}
                    />
                  </div>
                  {slackError && <p className="text-red-600 text-xs">{slackError}</p>}
                  <div className="flex gap-2">
                    <Button onClick={handleSaveSlack} disabled={slackSaving}>
                      {slackSaving ? "Saving…" : "Save"}
                    </Button>
                    <Button variant="secondary" onClick={() => { setEditingSlack(false); setSlackError(null); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </section>

            {/* Danger zone */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Danger zone</h3>
              <div className="border border-red-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  {selectedTenant.is_active
                    ? "Deactivating a tenant prevents its users from logging in."
                    : "Reactivating this tenant will allow its users to log in again."}
                </p>
                <Button
                  variant="danger"
                  onClick={() => handleToggleActive(selectedTenant)}
                >
                  {selectedTenant.is_active ? "Deactivate tenant" : "Reactivate tenant"}
                </Button>
              </div>
            </section>
          </>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Tenants</h2>
          <p className="text-sm text-gray-500">Manage isolated tenant organisations on this platform.</p>
        </div>
        <Button onClick={() => setView("create")}>
          <Plus className="w-4 h-4 mr-1" /> New tenant
        </Button>
      </div>

      <div className="border rounded-lg divide-y overflow-hidden">
        {tenants.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">No tenants found.</p>
        ) : (
          tenants.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => openTenant(t)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            >
              <div>
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-xs text-gray-500 font-mono">{t.slug}</p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    t.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {t.is_active ? "Active" : "Inactive"}
                </span>
                <ChevronLeft className="w-4 h-4 text-gray-400 rotate-180" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
